import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Tabs, Tab } from "@material-ui/core";
import { useHistory, useLocation, Route, Switch } from "react-router-dom";
import SendIcon from "@material-ui/icons/Send";
import PeopleIcon from "@material-ui/icons/People";
import SettingsIcon from "@material-ui/icons/Settings";
import Campaigns from "../Campaigns";
import ContactLists from "../ContactLists";
import CampaignsConfig from "../CampaignsConfig";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1, width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    overflow: "hidden", boxSizing: "border-box",
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)",
  },

  // ── HERO ──────────────────────────────────────────────────────────────────
  hero: {
    flexShrink: 0,
    padding: theme.spacing(3, 1.75, 0),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.25, 1.25, 0),
    },
  },
  heroPaper: {
    padding: 0,
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    position: "relative",
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  heroBgCircle: {
    position: "absolute", right: -30, top: -30,
    width: 180, height: 180, borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    minWidth: 0,
    zIndex: 1,
  },
  heroIcon: {
    width: 56, height: 56, borderRadius: 16, flexShrink: 0,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    boxShadow: "0 8px 20px rgba(0,102,255,0.35)", fontSize: "1.5rem",
  },
  heroBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "rgba(0,208,132,0.12)", border: "1px solid rgba(0,208,132,0.3)",
    borderRadius: 18, padding: "2px 10px",
    fontSize: "0.65rem", fontWeight: 700, color: "#00A86B", marginBottom: 5,
  },
  heroBadgeDot: {
    width: 5, height: 5, borderRadius: "50%",
    background: "#00D084", boxShadow: "0 0 5px rgba(0,208,132,0.8)",
  },
  heroTitle: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 800,
    fontSize: "clamp(21px, 2vw, 27px)",
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  heroTitleAccent: { color: "#0066FF" },
  heroSubtitle: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 400,
    fontSize: "12.5px",
    lineHeight: 1.5,
    marginTop: 4,
    color: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.56)"
      : "rgba(10,22,40,0.58)",
  },
  heroStats: { display: "flex", gap: theme.spacing(1.5), zIndex: 1 },
  heroStatCard: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.85)",
    border: "1px solid rgba(0,102,255,0.12)",
    borderRadius: 12, padding: theme.spacing(1, 2), textAlign: "center",
    backdropFilter: "blur(8px)",
  },
  heroStatNum: {
    fontWeight: 800, fontSize: "1.3rem", lineHeight: 1,
    color: theme.palette.type === "dark" ? "#fff" : "#0A1628",
  },
  heroStatLabel: {
    fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.4px", marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.4)" : "rgba(10,22,40,0.5)",
  },

  // ── TABS ──────────────────────────────────────────────────────────────────
  tabsWrap: { flexShrink: 0, padding: theme.spacing(1.5, 2.5, 0) },
  tabsBar: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,102,255,0.1)",
    borderRadius: 14, padding: theme.spacing(0.5),
    display: "inline-flex", minHeight: "unset",
  },
  tab: {
    borderRadius: 10, minHeight: 38, fontWeight: 700,
    fontSize: "0.8rem", textTransform: "none",
    padding: theme.spacing(0.6, 2.5), minWidth: "unset",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.5)",
    transition: "all 0.18s",
    "&.Mui-selected": {
      background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
      color: "#ffffff !important",
      boxShadow: "0 4px 12px rgba(0,102,255,0.3)",
    },
  },

  // ── CONTENT — esconde headers das páginas filhas ───────────────────────────
  tabContent: {
    flex: 1, minHeight: 0, overflow: "hidden",
    display: "flex", flexDirection: "column",
    padding: theme.spacing(1.5, 2.5),

    // Esconde o pageHeader/MainHeader de cada página filha
    "& > div > div > [class*='pageHeader']": { display: "none !important" },
    "& header": { display: "none !important" },
    "& [class*='pageHeader']": { display: "none !important" },
    "& [class*='MuiToolbar']": { display: "none !important" },

    // Faz o pageRoot das filhas ocupar 100%
    "& > div": { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
    "& > div > div[class*='pageRoot']": {
      padding: "0 !important",
      height: "100% !important",
      background: "transparent !important",
    },
  },
}));

const TABS = [
  { label: "Campanhas", path: "/disparos", icon: <SendIcon style={{ fontSize: 14 }} /> },
  { label: "Contatos", path: "/disparos/listas", icon: <PeopleIcon style={{ fontSize: 14 }} /> },
  { label: "Configurações", path: "/disparos/config", icon: <SettingsIcon style={{ fontSize: 14 }} /> },
];

const Disparos = () => {
  const classes  = useStyles();
  const history  = useHistory();
  const location = useLocation();

  const activeTab = Math.max(0, TABS.findIndex(t => t.path === location.pathname));

  return (
    <div className={classes.pageRoot}>

      {/* HERO */}
      <div className={classes.hero}>
        <div className={classes.heroPaper}>
          <div className={classes.heroLeft}>
            <div>
              <Typography className={classes.heroTitle}>
                {"Disparos WhatsApp"}
              </Typography>
              <Typography className={classes.heroSubtitle}>
                Gerencie campanhas, listas e configurações de envio com segurança.
              </Typography>
            </div>
          </div>
          <div className={classes.heroStats}>
            <div className={classes.heroStatCard}>
              <div className={classes.heroStatNum} style={{ color: "#0066FF" }}>WA</div>
              <div className={classes.heroStatLabel}>WhatsApp</div>
            </div>
            <div className={classes.heroStatCard}>
              <div className={classes.heroStatNum} style={{ color: "#00A86B" }}>24h</div>
              <div className={classes.heroStatLabel}>Operando</div>
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className={classes.tabsWrap}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => history.push(TABS[v].path)}
          className={classes.tabsBar}
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {TABS.map((t, i) => (
            <Tab
              key={i}
              className={classes.tab}
              label={t.label}
              icon={React.cloneElement(t.icon, { style: { fontSize: 14, marginBottom: 0, marginRight: 6 } })}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </div>

      {/* CONTEUDO */}
      <div className={classes.tabContent}>
        <Switch>
          <Route exact path="/disparos/listas">
            <ContactLists />
          </Route>
          <Route exact path="/disparos/config">
            <CampaignsConfig />
          </Route>
          <Route path="/disparos">
            <Campaigns />
          </Route>
        </Switch>
      </div>

    </div>
  );
};

export default Disparos;
